// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, useRef, useState, type FormEvent } from 'react';

import { authApi } from '../../api/auth';
import type { CaseGroupShare, Group, ShareRole } from '../../api/types';
import { SHARE_ROLE_LABELS, SHARE_ROLES } from '../../app/auth';
import { useArmedConfirm } from '../../app/useArmedConfirm';
import { useAsync } from '../../app/useAsync';
import { useMutationQueue } from '../../app/useMutationQueue';
import {
  canManageGroupShares,
  type Caller,
  type GroupShareAction,
  groupShareErrorText,
  memberCountText,
  offerableGroups,
  sortGroupShares,
} from '../../deling/model';
import { Button } from '../Button';
import { DataTable, type Column } from '../DataTable';
import { ErrorBanner } from '../ErrorBanner';
import { Select } from '../Select';
import { Spinner } from '../Spinner';
import controls from '../controls.module.css';
import styles from './Deling.module.css';

const SHARE_ROLE_HINTS: Record<ShareRole, string> = {
  editor: 'Gruppens medlemmer kan ændre sagen, men ikke slette den eller styre adgang.',
  viewer: 'Gruppens medlemmer kan se sagen, men ikke ændre den.',
};

/**
 * Grupper — the groups a case is shared with (ruxd-wmc.3). Every member of a
 * shared group gets the share's role unless their own membership is higher.
 * Every member of the case sees the list; only the case's owners and an admin
 * share with a group (any group), change a share's role or stop it.
 */
export function GroupSharesPanel({ cid, caller }: { cid: string; caller: Caller }) {
  const headingId = useId();
  const manage = canManageGroupShares(caller);
  const shares = useAsync<CaseGroupShare[]>((s) => authApi.caseGroups(cid, s), [cid]);
  // Only an owner or admin can share, so only they need the list of groups to offer.
  const groups = useAsync<Group[]>((s) => (manage ? authApi.groups(s) : Promise.resolve([])), [manage]);
  const [groupId, setGroupId] = useState('');
  const [role, setRole] = useState<ShareRole>('viewer');
  const [failure, setFailure] = useState<string | null>(null);
  const action = useRef<GroupShareAction>('share');

  const { busy, mutate } = useMutationQueue({
    scope: 'page',
    onError: (cause) => setFailure(groupShareErrorText(cause, action.current)),
    onSettled: () => {
      shares.reload();
      groups.reload();
    },
  });
  const confirm = useArmedConfirm<string>(busy, shares.data);

  if (shares.error) return <ErrorBanner error={shares.error as Error} onRetry={shares.reload} context="sagens grupper" />;
  if (!shares.data) return <Spinner label="Indlæser grupper…" />;

  const list = sortGroupShares(shares.data);
  const offer = offerableGroups(groups.data ?? [], list);
  const picked = offer.some((g) => g.id === groupId) ? groupId : (offer[0]?.id ?? '');

  const share = (e: FormEvent) => {
    e.preventDefault();
    if (!picked || busy) return;
    setFailure(null);
    action.current = 'share';
    void mutate(async () => {
      await authApi.shareWithGroup(cid, picked, role);
      setGroupId('');
    });
  };

  const changeRole = (s: CaseGroupShare, next: ShareRole) => {
    setFailure(null);
    action.current = 'role';
    void mutate(async () => {
      await authApi.setGroupShareRole(cid, s.group.id, next);
    });
  };

  const remove = (s: CaseGroupShare) => {
    setFailure(null);
    confirm.disarm();
    action.current = 'unshare';
    void mutate(async () => {
      await authApi.unshareGroup(cid, s.group.id);
    });
  };

  const columns: Column<CaseGroupShare>[] = [
    {
      key: 'group',
      header: 'Gruppe',
      render: (s) => (
        <span className={styles.name}>
          {s.group.name}
          <span className={styles.sub}>{memberCountText(s.group.member_count)}</span>
        </span>
      ),
    },
    {
      key: 'role',
      header: 'Rolle',
      render: (s) =>
        manage ? (
          <Select
            variant="outline"
            size="sm"
            value={s.role}
            disabled={busy}
            aria-label={`Rolle for ${s.group.name}`}
            onChange={(e) => changeRole(s, e.target.value as ShareRole)}
          >
            {SHARE_ROLES.map((r) => (
              <option key={r} value={r}>
                {SHARE_ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        ) : (
          <span className={styles.role}>{SHARE_ROLE_LABELS[s.role]}</span>
        ),
    },
  ];
  if (manage)
    columns.push({
      key: 'remove',
      header: '',
      render: (s) =>
        confirm.armed === s.group.id ? (
          <Button
            variant="outline"
            tone="danger"
            size="sm"
            armed
            disabled={busy}
            onClick={() => remove(s)}
            onBlur={confirm.disarm}
          >
            Stop deling med {s.group.name}?
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={(e) => confirm.arm(s.group.id, e.currentTarget)}
          >
            Stop deling
          </Button>
        ),
    });

  return (
    <section className={styles.panel} aria-labelledby={headingId}>
      <div className={styles.panelHead}>
        <h2 id={headingId} className={styles.panelHeading}>
          Grupper
        </h2>
        <span className={styles.count}>{list.length}</span>
      </div>
      <p className={styles.muted}>
        Del sagen med en hel gruppe. Gruppens medlemmer får rollen herunder, medmindre de selv har en højere rolle i
        sagen. En gruppe kan ikke eje en sag.
      </p>

      <DataTable
        columns={columns}
        rows={list}
        rowKey={(s) => s.group.id}
        empty={<p className={styles.muted}>Sagen er ikke delt med nogen grupper.</p>}
      />

      {manage && offer.length > 0 && (
        <form className={styles.row} onSubmit={share}>
          <label className={controls.field}>
            <span className={controls.fieldLabel}>Del med gruppe</span>
            <Select variant="outline" size="sm" value={picked} onChange={(e) => setGroupId(e.target.value)}>
              {offer.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} ({memberCountText(g.member_count)})
                </option>
              ))}
            </Select>
          </label>
          <label className={`${controls.field} ${styles.narrow}`}>
            <span className={controls.fieldLabel}>Rolle</span>
            <Select variant="outline" size="sm" value={role} onChange={(e) => setRole(e.target.value as ShareRole)}>
              {SHARE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {SHARE_ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </label>
          <Button type="submit" variant="primary" size="sm" disabled={busy || !picked}>
            Del
          </Button>
          <span className={styles.hint}>{SHARE_ROLE_HINTS[role]}</span>
        </form>
      )}
      {manage && groups.data && offer.length === 0 && (
        <p className={styles.muted}>
          {groups.data.length === 0 ? 'Der er ingen grupper at dele med endnu.' : 'Sagen er allerede delt med alle grupper.'}
        </p>
      )}
      {manage && groups.error && <p className={styles.error}>Kunne ikke hente grupperne.</p>}
      {!manage && <p className={styles.muted}>Kun sagens ejere og administratorer kan dele sagen med en gruppe.</p>}
      {failure && (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}
    </section>
  );
}
